import React from 'react';
import { Box, Typography } from '@mui/material';

interface RichTextBlockProps {
  data: {
    content: string;
    alignment?: 'left' | 'center' | 'right';
    backgroundColor?: string;
    backgroundImage?: string;
    overlayOpacity?: number;
    fullWidth?: boolean;
    maxWidth?: 'narrow' | 'standard' | 'wide' | 'full';
  };
  theme?: {
    primaryColor: string;
    accentColor: string;
  };
}

const RichTextBlock: React.FC<RichTextBlockProps> = ({ data, theme }) => {
  const {
    content,
    alignment = 'left',
    backgroundColor,
    backgroundImage,
    overlayOpacity = 0.5,
    fullWidth = false,
    maxWidth = 'standard',
  } = data;

  if (!content) return null;

  const getMaxWidth = () => {
    if (fullWidth) return '100%';

    switch (maxWidth) {
      case 'narrow':
        return '900px';
      case 'standard':
        return '1200px';
      case 'wide':
        return '1400px';
      case 'full':
        return '100%';
      default:
        return '1200px';
    }
  };

  return (
    <Box
      id="rich-text"
      sx={{
        position: 'relative',               // ✅ REQUIRED
        overflow: 'hidden',                 // ✅ REQUIRED
        py: { xs: 6, md: 10 },
        px: { xs: 3, md: 6 },
        mx: fullWidth ? 0 : 'auto',
        maxWidth: getMaxWidth(),
        backgroundColor: backgroundColor || 'transparent',
        backgroundImage: backgroundImage ? `url(${backgroundImage})` : 'none',
        backgroundSize: backgroundImage ? 'cover' : undefined,
        backgroundPosition: backgroundImage ? 'center' : undefined,
        backgroundRepeat: backgroundImage ? 'no-repeat' : undefined,
        borderRadius: fullWidth ? 0 : 2,
      }}
    >
      {/* Overlay */}
     {backgroundImage && overlayOpacity !== 0 && (
  <Box
    sx={{
      position: 'absolute',
      inset: 0,
      bgcolor: overlayOpacity > 0 ? 'black' : 'white',
      opacity: Math.abs(overlayOpacity),
      zIndex: 0
    }}
  />
)}

      <Typography
        component="div"
        sx={{
          position: 'relative',   // ✅ REQUIRED
          zIndex: 1,              // ✅ REQUIRED

          fontSize: '1.125rem',
          lineHeight: 1.8,
          color: '#000000',
          textAlign: alignment,
          whiteSpace: 'pre-wrap',

          '& p': { mb: 2 },
          '& h1, & h2, & h3, & h4, & h5, & h6': {
            fontWeight: 700,
            color: theme?.primaryColor || '#000000',
            mb: 2,
            mt: 3,
            lineHeight: 1.3,
          },
          '& h1': { fontSize: '4rem' },
          '& h2': { fontSize: '2.5rem' },
          '& h3': { fontSize: '1.75rem' },
          '& h4': { fontSize: '1.25rem' },
          '& h5': { fontSize: '1.1rem' },
          '& h6': { fontSize: '1rem' },

          '& ul, & ol': {
            pl: 4,
            mb: 2,
          },
          '& li': { mb: 1 },

          '& strong': {
            fontWeight: 700,
            color: theme?.primaryColor || '#000000',
          },
          '& em': { fontStyle: 'italic' },

          '& a': {
            color: theme?.accentColor || '#3A9188',
            textDecoration: 'none',
            fontWeight: 500,
            '&:hover': { textDecoration: 'underline' },
          },

          '& img': {
            maxWidth: '100%',
            borderRadius: 2,
            display: 'block',
            mx: 'auto',
            my: 3,
          },
          '& iframe': {
            width: '100%',
            aspectRatio: '16/9',
            border: 0,
            borderRadius: 2,
            my: 3,
          },

          '& hr': {
            border: 0,
            borderTop: '1px solid #ccc',
            my: 4,
          },
          '& blockquote': {
            borderLeft: `4px solid ${theme?.accentColor || '#3A9188'}`,
            pl: 3,
            fontStyle: 'italic',
            color: 'text.secondary',
            my: 3,
          },
        }}
        dangerouslySetInnerHTML={{ __html: content }}
      />
    </Box>
  );
};

export default RichTextBlock;